import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import DataTable from './Common/DataTable';
import Modal from './Common/Modal';
import FeeChallan from './FeeChallan';
import BulkFeeChallan from './BulkFeeChallan';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCY } from '../config';
import { buildStatement, getPreviousDuesFromList } from '../utils/challan';

const FEE_TYPES = ['Tuition', 'Transport', 'Library', 'Exam', 'Admission', 'Other'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function studentToChallanShape(student) {
  if (!student) return null;
  return {
    name: student.student_name || student.name || 'Unknown',
    class_name: student.class_name || '—',
    campus_name: student.campus_name || '—',
    registration_number: student.registration_number || '—',
  };
}

function deriveStatus(fee) {
  const balance = (fee.amount || 0) - (fee.paid_amount || 0);
  if (balance <= 0.01) return 'Paid';
  if (fee.due_date && new Date(fee.due_date) < new Date()) return 'Overdue';
  return 'Pending';
}

// ═══════════════════════════════════════════════════════════════════
// Standalone printable HTML for the Challan Details PDF.
// ═══════════════════════════════════════════════════════════════════
function buildChallanDetailsHTML(fee, student, stmt) {
  const monthsWithItems = stmt.months.filter(m => m.items.length > 0);
  const monthsOutstanding = monthsWithItems.length;
  const oldestUnpaid = monthsWithItems.length > 0
    ? monthsWithItems[monthsWithItems.length - 1].monthLabel
    : '—';
  const totalTuition = monthsWithItems
    .flatMap(m => m.items)
    .filter(it => it.kind !== 'late')
    .reduce((s, it) => s + (it.balance || 0), 0);
  const totalLate = monthsWithItems
    .flatMap(m => m.items)
    .filter(it => it.kind === 'late')
    .reduce((s, it) => s + (it.balance || 0), 0);
  const totalLateCount = monthsWithItems
    .flatMap(m => m.items)
    .filter(it => it.kind === 'late')
    .length;

  const feeBase = fee.base_amount ?? fee.amount ?? 0;
  const feeNet = fee.amount || 0;
  const feeDiscPct = fee.discount_percentage || 0;
  const feeIsWaived = feeNet <= 0.01 && feeBase > 0.01;
  const feeIsPaid = !feeIsWaived && (fee.status === 'Paid' || ((fee.amount || 0) - (fee.paid_amount || 0)) <= 0.01);

  let statusStrip = '';
  if (feeIsPaid) {
    statusStrip = `
      <div class="status-strip paid">
        <div class="status-line">
          <div class="status-left">
            <span class="status-icon">✓</span>
            <div>
              <div class="status-title">PAID</div>
              <div class="status-meta">${fee.paid_date || '—'}${fee.payment_method ? ' · ' + fee.payment_method : ''}</div>
            </div>
          </div>
          <div class="status-amount">Rs ${Number(fee.paid_amount || 0).toFixed(2)}</div>
        </div>
        ${feeDiscPct > 0 ? `
          <div class="status-breakdown">
            <div>
              <div class="bd-label">Gross</div>
              <div class="bd-value">Rs ${feeBase.toFixed(2)}</div>
            </div>
            <div>
              <div class="bd-label">Discount (${feeDiscPct}%)</div>
              <div class="bd-value red">−Rs ${(feeBase - feeNet).toFixed(2)}</div>
            </div>
            <div>
              <div class="bd-label">Net Paid</div>
              <div class="bd-value">Rs ${feeNet.toFixed(2)}</div>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  } else if (feeIsWaived) {
    statusStrip = `
      <div class="status-strip waived">
        <div class="status-line">
          <div class="status-left">
            <span class="status-icon purple">★</span>
            <div>
              <div class="status-title purple">FULLY DISCOUNTED</div>
              <div class="status-meta">${fee.paid_date || '—'}</div>
            </div>
          </div>
          <div class="status-amount purple">Rs 0.00</div>
        </div>
        <div class="status-breakdown">
          <div>
            <div class="bd-label">Gross</div>
            <div class="bd-value">Rs ${feeBase.toFixed(2)}</div>
          </div>
          <div>
            <div class="bd-label">Discount (${feeDiscPct}%)</div>
            <div class="bd-value red">−Rs ${(feeBase - feeNet).toFixed(2)}</div>
          </div>
          <div>
            <div class="bd-label">Net Waived</div>
            <div class="bd-value purple">Rs 0.00</div>
          </div>
        </div>
      </div>
    `;
  }

  const monthBlocks = monthsWithItems.map(m => `
    <div class="month">
      <div class="month-header">
        ${m.monthLabel}${m.isCurrent ? ' <span class="badge">Current</span>' : ''}
      </div>
      <table>
        <thead>
          <tr>
            <th class="desc-col">Description</th>
            <th class="amount-col right">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${m.items.map(it => `
            <tr class="${it.kind === 'late' ? 'late' : ''}">
              <td class="desc-col">${it.label}</td>
              <td class="amount-col right ${it.kind === 'late' ? 'late-text' : ''}">Rs ${it.balance.toFixed(2)}</td>
            </tr>
          `).join('')}
          <tr class="subtotal">
            <td class="desc-col">${m.monthLabel} Subtotal</td>
            <td class="amount-col right">Rs ${m.subtotal.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  `).join('');

  const today = new Date().toLocaleDateString();
  const nowStr = new Date().toLocaleString();

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Challan Details — ${student?.student_name || ''}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; padding: 24px; color: #111; font-size: 12px; }
  h1 { font-size: 20px; margin-bottom: 2px; }
  .subtitle { color: #666; font-size: 11px; margin-bottom: 16px; }
  .meta { background: #f5f5f5; padding: 12px; border-radius: 4px; margin-bottom: 14px; }
  .meta-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
  .meta-label { color: #666; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
  .meta-value { font-weight: 700; margin-top: 3px; font-size: 12px; }

  .status-strip { margin-bottom: 14px; padding: 12px 14px; border-radius: 4px; border-width: 1px; border-style: solid; }
  .status-strip.paid { background: #f0fdf4; border-color: #16a34a; }
  .status-strip.waived { background: #faf5ff; border-color: #9333ea; }
  .status-line { display: flex; align-items: center; justify-content: space-between; }
  .status-left { display: flex; align-items: center; gap: 10px; }
  .status-icon { display: inline-block; width: 22px; height: 22px; line-height: 22px; text-align: center; border-radius: 50%; background: #16a34a; color: white; font-weight: 700; font-size: 13px; }
  .status-icon.purple { background: #9333ea; }
  .status-title { font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; font-size: 11px; color: #166534; }
  .status-title.purple { color: #6b21a8; }
  .status-meta { color: #166534; font-size: 11px; margin-top: 1px; }
  .status-strip.waived .status-meta { color: #6b21a8; }
  .status-amount { font-weight: 700; font-size: 16px; color: #166534; }
  .status-amount.purple { color: #6b21a8; }
  .status-breakdown { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(0,0,0,0.1); }
  .bd-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
  .bd-value { font-size: 13px; font-weight: 700; margin-top: 3px; }
  .bd-value.red { color: #dc2626; }
  .bd-value.purple { color: #6b21a8; }

  .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
  .summary-card { padding: 10px 12px; border-radius: 4px; background: #f5f5f5; }
  .summary-card .label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
  .summary-card .value { font-size: 18px; font-weight: 700; margin-top: 4px; }
  .summary-card.tuition { background: #dbeafe; }
  .summary-card.tuition .label { color: #1e40af; }
  .summary-card.tuition .value { color: #1e40af; }
  .summary-card.late { background: #fef3c7; }
  .summary-card.late .label { color: #92400e; }
  .summary-card.late .value { color: #92400e; }
  .grand-total { display: flex; justify-content: space-between; align-items: center; background: #fee2e2; border: 2px solid #dc2626; padding: 12px 14px; border-radius: 4px; margin-bottom: 18px; }
  .grand-total .label { color: #991b1b; font-weight: 700; text-transform: uppercase; font-size: 11px; letter-spacing: 0.05em; }
  .grand-total .value { color: #991b1b; font-weight: 700; font-size: 22px; }
  .month { margin-bottom: 12px; page-break-inside: avoid; }
  .month-header { font-weight: 700; text-transform: uppercase; font-size: 10px; letter-spacing: 0.05em; color: #333; margin-bottom: 5px; }
  .badge { background: #dbeafe; color: #1e40af; padding: 1px 6px; border-radius: 3px; font-size: 9px; margin-left: 6px; letter-spacing: normal; text-transform: none; }

  table { width: 100%; border-collapse: collapse; font-size: 11px; table-layout: fixed; }
  th { background: #f3f4f6; text-align: left; padding: 6px 10px; border: 1px solid #d1d5db; font-weight: 600; }
  td { padding: 6px 10px; border: 1px solid #d1d5db; }
  .desc-col { width: auto; text-align: left; }
  .amount-col { width: 140px; text-align: right; }
  .right { text-align: right; }
  tr.late td { background: #fef3c7; color: #92400e; }
  tr.late td.late-text { font-weight: 700; }
  tr.subtotal td { background: #f9fafb; font-weight: 700; }
  .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #d1d5db; font-size: 10px; color: #666; }
  @media print {
    body { padding: 12px; }
    @page { size: A4 portrait; margin: 10mm; }
  }
</style>
</head>
<body>
  <h1>Challan Details</h1>
  <div class="subtitle">${student?.student_name || ''} · ${student?.class_name || ''} · ${student?.registration_number || ''}</div>

  <div class="meta">
    <div class="meta-grid">
      <div>
        <div class="meta-label">Student</div>
        <div class="meta-value">${student?.student_name || '—'}</div>
      </div>
      <div>
        <div class="meta-label">Reg #</div>
        <div class="meta-value">${student?.registration_number || '—'}</div>
      </div>
      <div>
        <div class="meta-label">Class</div>
        <div class="meta-value">${student?.class_name || '—'}</div>
      </div>
      <div>
        <div class="meta-label">Generated</div>
        <div class="meta-value">${today}</div>
      </div>
    </div>
  </div>

  ${statusStrip}

  <div class="summary">
    <div class="summary-card">
      <div class="label">Months Outstanding</div>
      <div class="value">${monthsOutstanding}</div>
    </div>
    <div class="summary-card">
      <div class="label">Oldest Unpaid</div>
      <div class="value" style="font-size:14px;">${oldestUnpaid}</div>
    </div>
    <div class="summary-card tuition">
      <div class="label">Total Tuition</div>
      <div class="value">Rs ${totalTuition.toFixed(2)}</div>
    </div>
    <div class="summary-card late">
      <div class="label">Total Late Fees${totalLateCount > 0 ? ` (${totalLateCount})` : ''}</div>
      <div class="value">Rs ${totalLate.toFixed(2)}</div>
    </div>
  </div>

  <div class="grand-total">
    <span class="label">Grand Total Payable</span>
    <span class="value">Rs ${stmt.grandTotal.toFixed(2)}</span>
  </div>

  ${monthBlocks}

  <div class="footer">
    <strong>Note:</strong> This is a cumulative fee statement generated on ${nowStr}. All outstanding fees are included in the grand total.
  </div>
</body>
</html>`;
}

export default function FeeManagement() {
  const { user } = useAuth();

  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [payModal, setPayModal] = useState(null);
  const [genModal, setGenModal] = useState(false);
  const [genLoading, setGenLoading] = useState(false);

  const [editFeeModal, setEditFeeModal] = useState(null);
  const [editFeeForm, setEditFeeForm] = useState({
    base_amount: 0, discount_percentage: 0, discount_reason: '', amount: 0,
  });

  const now = new Date();
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);
  const [viewYear, setViewYear] = useState(now.getFullYear());

  const [genForm, setGenForm] = useState({
    fee_types: ['Tuition'],
    class_id: '',
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    due_date: '',
    amount_mode: 'structure',
    amount: '',
    apply_discounts: true,
    notes: '',
  });

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [search, setSearch] = useState('');

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [challanFee, setChallanFee] = useState(null);
  const [bulkChallans, setBulkChallans] = useState(null);

  const [form, setForm] = useState({
    student_id: '', fee_type: 'Tuition', amount: 0, due_date: '', notes: '',
  });

  const [payment, setPayment] = useState({
    paid_date: new Date().toISOString().split('T')[0],
    payment_method: 'Cash',
    notes: '',
  });

  useEffect(() => { 
    fetchData(); 
  }, []);

  useEffect(() => { setSelectedIds(new Set()); }, [viewMonth, viewYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [feesRes, studentsRes, classesRes, structuresRes] = await Promise.all([
        api.get('/fees/'),
        api.get('/students/'),
        api.get('/classes/').catch(() => ({ data: [] })),
        api.get('/fees/structures').catch(() => ({ data: [] })),
      ]);
      setFees(feesRes.data || []);
      setStudents(studentsRes.data || []);
      setClasses(classesRes.data || []);
      setStructures(structuresRes.data || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleCreateFee = async (e) => {
    e.preventDefault();
    try {
      await api.post('/fees/', {
        student_id: parseInt(form.student_id),
        fee_type: form.fee_type,
        amount: parseFloat(form.amount),
        base_amount: parseFloat(form.amount),
        due_date: form.due_date,
        paid_amount: 0,
        status: 'Pending',
        notes: form.notes || '',
      });
      setModalOpen(false);
      setForm({ student_id: '', fee_type: 'Tuition', amount: 0, due_date: '', notes: '' });
      fetchData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openGenerateModal = () => {
    const dueDay = 5;
    const dueDateStr = `${viewYear}-${String(viewMonth).padStart(2, '0')}-${String(dueDay).padStart(2, '0')}`;
    setGenForm(f => ({ ...f, month: viewMonth, year: viewYear, due_date: dueDateStr }));
    setGenModal(true);
  };

  const handleGenerateBatch = async () => {
    if (!genForm.due_date) { alert('Please pick a due date.'); return; }
    
    if (genForm.fee_types.length === 0) {
      alert('Please select at least one fee type.');
      return;
    }

    if (genForm.amount_mode === 'custom' && (!genForm.amount || parseFloat(genForm.amount) <= 0)) {
      alert('Enter a valid custom amount.');
      return;
    }
    const payload = {
      fee_types: genForm.fee_types,
      class_id: genForm.class_id ? parseInt(genForm.class_id) : null,
      month: genForm.month,
      year: genForm.year,
      due_date: genForm.due_date,
      amount: genForm.amount_mode === 'custom' ? parseFloat(genForm.amount) : null,
      apply_discounts: genForm.apply_discounts,
      notes: genForm.notes || '',
    };
    setGenLoading(true);
    try {
      const res = await api.post('/fees/generate-batch', payload);
      const d = res.data;
      alert(
        `${d.message}\n\n` +
        `Created: ${d.created}\n` +
        `Skipped (already exist): ${d.skipped}\n` +
        `No fee structure found: ${d.no_structure}\n` +
        `Total students matched: ${d.total_students}`
      );
      setGenModal(false);
      setViewMonth(genForm.month);
      setViewYear(genForm.year);
      fetchData();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenLoading(false);
    }
  };

  const openEditFee = (fee) => {
    setEditFeeModal(fee);
    setEditFeeForm({
      base_amount: fee.base_amount ?? fee.amount,
      discount_percentage: fee.discount_percentage ?? 0,
      discount_reason: fee.discount_reason || '',
      amount: fee.amount,
    });
  };

  const handleSaveEditFee = async () => {
    try {
      await api.put(`/fees/${editFeeModal.id}`, {
        base_amount: parseFloat(editFeeForm.base_amount),
        discount_percentage: parseFloat(editFeeForm.discount_percentage),
        discount_reason: editFeeForm.discount_reason || null,
      });
      setEditFeeModal(null);
      fetchData();
      alert('✓ Fee updated');
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownloadDetails = (fee, student, stmt) => {
    const html = buildChallanDetailsHTML(fee, student, stmt);
    const w = window.open('', '_blank', 'width=1000,height=800');
    if (!w) {
      alert('Popup blocked. Please allow popups for this site to download challan details.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      try { w.print(); } catch (e) { /* user closed window */ }
    }, 400);
  };

  const handleRecordFullPayment = async () => {
    if (!payModal) return;

    const student = students.find(s => s.id === payModal.student_id);
    const stmt = buildStatement(fees, payModal, structures, student);

    if (stmt.grandTotal <= 0.01) {
      alert('Nothing to pay for this student.');
      return;
    }
    if (!window.confirm(
      `Record full payment of ${CURRENCY}${stmt.grandTotal.toFixed(2)}?\n\n` +
      `This clears ALL outstanding fees for this student.`
    )) return;

    const today = payment.paid_date;
    const method = payment.payment_method;

    try {
      for (const lf of stmt.lateFeesToCreate) {
        await api.post('/fees/', { ...lf, paid_date: today, payment_method: method });
      }
      for (const row of stmt.itemsToMarkPaid) {
        await api.put(`/fees/${row.id}`, {
          paid_amount: row.payAmount,
          paid_date: today,
          payment_method: method,
          status: 'Paid',
        });
      }

      alert(`✓ Payment recorded: ${CURRENCY}${stmt.grandTotal.toFixed(2)}`);
      setPayModal(null);
      setPayment({
        paid_date: new Date().toISOString().split('T')[0],
        payment_method: 'Cash',
        notes: '',
      });
      fetchData();
    } catch (err) {
      alert('Payment failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleBulkMarkPaid = async () => {
    const selected = data.filter(row => selectedIds.has(row._feeId));
    const pending = selected.filter(row => row._status !== 'Paid');
    if (pending.length === 0) { alert('No pending fees selected'); return; }
    if (!window.confirm(
      `Mark ${pending.length} fee(s) as fully paid?\n\nTotal: ${CURRENCY}${pending.reduce((s, r) => s + r._balance, 0).toFixed(2)}`
    )) return;

    let successCount = 0;
    let errorCount = 0;
    const today = new Date().toISOString().split('T')[0];
    for (const row of pending) {
      try {
        await api.put(`/fees/${row._feeId}`, {
          paid_amount: row._finalAmount,
          paid_date: today,
          payment_method: 'Bulk',
          status: 'Paid',
        });
        successCount++;
      } catch { errorCount++; }
    }
    alert(`✓ Marked ${successCount} paid${errorCount > 0 ? ` · ✗ ${errorCount} failed` : ''}`);
    setSelectedIds(new Set());
    fetchData();
  };

  const handleOpenBulkChallans = () => {
    const items = data
      .filter(r => selectedIds.has(r._feeId))
      .map(r => {
        const fee = fees.find(f => f.id === r._feeId);
        const studentRaw = students.find(s => s.id === fee?.student_id);
        return {
          fee,
          student: studentToChallanShape(studentRaw),
          previousDues: getPreviousDuesFromList(fees, fee),
        };
      })
      .filter(x => x.fee && x.student);
    if (items.length === 0) {
      alert('No valid challans to print.');
      return;
    }
    setBulkChallans(items);
  };

  const goPrevMonth = () => {
    if (viewMonth === 1) { setViewMonth(12); setViewYear(viewYear - 1); }
    else { setViewMonth(viewMonth - 1); }
  };
  const goNextMonth = () => {
    if (viewMonth === 12) { setViewMonth(1); setViewYear(viewYear + 1); }
    else { setViewMonth(viewMonth + 1); }
  };
  const goCurrentMonth = () => {
    setViewMonth(now.getMonth() + 1);
    setViewYear(now.getFullYear());
  };
  const isCurrentMonth = viewMonth === now.getMonth() + 1 && viewYear === now.getFullYear();

  const monthFilteredFees = fees.filter(f => {
    if (!f.due_date) return false;
    const d = new Date(f.due_date);
    return d.getMonth() + 1 === viewMonth && d.getFullYear() === viewYear;
  });

  // 🔴 SAFE FILTER: Wrapped in try/catch to prevent crashing the whole page
  const filtered = monthFilteredFees.filter(f => {
    const student = students.find(s => s.id === f.student_id);
    
    let totalDue = f.amount - (f.paid_amount || 0);
    let displayStatus = 'Paid';

    try {
      if (structures && structures.length > 0) {
        const stmt = buildStatement(fees, f, structures, student);
        totalDue = stmt.months.reduce((s, m) => s + m.subtotal, 0);
      }
      if (totalDue > 0.01) displayStatus = 'Overdue';
    } catch (err) {
      console.warn("buildStatement failed, using fallback for", student?.student_name);
      if (totalDue > 0.01) displayStatus = 'Overdue';
    }

    if (filterStatus !== 'all' && displayStatus !== filterStatus) return false;
    if (filterType !== 'all' && f.fee_type !== filterType) return false;
    if (filterClass !== 'all') {
      const cls = classes.find(c => c.id === parseInt(filterClass));
      if (!cls) return false;
      if (student?.class_name !== cls.name) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const blob = `${student?.student_name || ''} ${student?.registration_number || ''} ${student?.roll_no || ''} ${f.fee_type}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const totalAmount = monthFilteredFees.reduce((s, f) => s + (f.amount || 0), 0);
  const totalPaid = monthFilteredFees.reduce((s, f) => s + (f.paid_amount || 0), 0);
  const totalPending = totalAmount - totalPaid;
  const overdueCount = monthFilteredFees.filter(f => deriveStatus(f) === 'Overdue').length;

  const columns = [
    '', 'Student', 'Reg #', 'Class', 'Fee Type', 'Final', 'Paid', 'Balance',
    'Previous', 'Total Due', 'Due Date', 'Status', 'Challan', 'Actions',
  ];

  const data = filtered.map(f => {
    const student = students.find(s => s.id === f.student_id);
    const balance = (f.amount || 0) - (f.paid_amount || 0);
    
    let totalDue = balance;
    let prevBalance = 0;
    let stmt = null;

    // 🔴 SAFE CALCULATION: Wrapped in try/catch to prevent white screen
    try {
      if (structures && structures.length > 0) {
        stmt = buildStatement(fees, f, structures, student);
        prevBalance = stmt.months.filter(m => !m.isCurrent).reduce((s, m) => s + m.subtotal, 0);
        totalDue = stmt.months.reduce((s, m) => s + m.subtotal, 0);
      }
    } catch (err) {
      console.warn("buildStatement failed for table row, using fallback", student?.student_name);
      totalDue = balance;
    }

    let displayStatus = 'Paid';
    if (totalDue > 0.01) {
      displayStatus = 'Overdue';
    }
    
    const isPaid = totalDue <= 0.01;

    // We need to pass a valid stmt to handleDownloadDetails, so we create a mock one if it failed
    const safeStmt = stmt || { months: [], grandTotal: totalDue };

    return {
      _feeId: f.id,
      _status: displayStatus,
      _balance: balance,
      _finalAmount: f.amount,
      '': (
        <input
          type="checkbox"
          className="w-4 h-4 cursor-pointer"
          checked={selectedIds.has(f.id)}
          disabled={isPaid}
          onChange={(e) => {
            const next = new Set(selectedIds);
            if (e.target.checked) next.add(f.id); else next.delete(f.id);
            setSelectedIds(next);
          }}
        />
      ),
      Student: student?.student_name || `Student #${f.student_id}`,
      'Reg #': <span className="text-xs font-mono text-gray-600">{student?.registration_number || '—'}</span>,
      Class: <span className="text-xs">{student?.class_name || '—'}</span>,
      'Fee Type': f.fee_type,
      Final: <span className="font-bold">{CURRENCY}{f.amount}</span>,
      Paid: `${CURRENCY}${f.paid_amount || 0}`,
      Balance: `${CURRENCY}${balance.toFixed(2)}`,
      Previous: prevBalance > 0 ? (
        <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800">
          {CURRENCY}{prevBalance.toFixed(2)}
        </span>
      ) : <span className="text-xs text-gray-400">—</span>,
      'Total Due': totalDue > 0 ? (
        <span className="font-bold text-red-700">{CURRENCY}{totalDue.toFixed(2)}</span>
      ) : <span className="text-xs text-gray-400">—</span>,
      'Due Date': f.due_date,
      Status: (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          displayStatus === 'Paid' ? 'bg-green-100 text-green-800'
          : displayStatus === 'Overdue' ? 'bg-red-100 text-red-800'
          : 'bg-yellow-100 text-yellow-800'
        }`}>{displayStatus}</span>
      ),
      Challan: (
        <button
          onClick={() => setChallanFee(f)}
          className={`px-2 py-1 text-xs rounded font-semibold whitespace-nowrap ${
            isPaid
              ? 'bg-green-100 text-green-800 hover:bg-green-200'
              : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
          }`}
          title={isPaid ? 'View paid receipt' : 'View cumulative statement'}
        >
          <i className={`fas ${isPaid ? 'fa-check-circle' : 'fa-file-invoice'} mr-1`}></i>
          {isPaid ? 'Receipt' : 'Statement'}
        </button>
      ),
      Actions: (
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => handleDownloadDetails(f, student, safeStmt)}
            className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded hover:bg-indigo-200 whitespace-nowrap"
            title="Download challan details as PDF"
          >
            <i className="fas fa-file-pdf mr-1"></i> Details
          </button>

          <button
            onClick={() => openEditFee(f)}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
            title="Edit fee / discount"
          >
            <i className="fas fa-percent"></i>
          </button>

          {!isPaid ? (
            <button
              onClick={() => setPayModal(f)}
              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 whitespace-nowrap"
              title="Record full payment — clears all outstanding"
            >
              <i className="fas fa-money-bill-wave mr-1"></i> Pay Full
            </button>
          ) : (
            <span className="text-xs text-gray-400 px-2 py-1">Paid ✓</span>
          )}
        </div>
      ),
    };
  });

  const selectableIds = data.filter(r => r._status !== 'Paid').map(r => r._feeId);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const handleToggleAll = (e) => {
    if (e.target.checked) setSelectedIds(new Set(selectableIds));
    else setSelectedIds(new Set());
  };

  const selectedCount = data.filter(r => selectedIds.has(r._feeId)).length;
  const selectedBalance = data.filter(r => selectedIds.has(r._feeId)).reduce((s, r) => s + r._balance, 0);

  const challanStudent = challanFee
    ? studentToChallanShape(students.find(s => s.id === challanFee.student_id))
    : null;

  const payStatement = payModal ? (() => {
    const student = students.find(s => s.id === payModal.student_id);
    try {
      return buildStatement(fees, payModal, structures, student);
    } catch (e) {
      return { months: [], grandTotal: 0, itemsToMarkPaid: [], lateFeesToCreate: [] };
    }
  })() : null;

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">
          💰 Fee Management 
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={openGenerateModal}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm">
            <i className="fas fa-magic mr-1"></i> Generate Fees
          </button>
          <button onClick={() => setModalOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm">
            <i className="fas fa-plus mr-2"></i> Add Fee
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded shadow mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={goPrevMonth}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 font-medium border">
            <i className="fas fa-chevron-left mr-1"></i> Prev
          </button>
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800">📅 {MONTHS[viewMonth - 1]} {viewYear}</h3>
            <div className="flex items-center justify-center gap-2 mt-1">
              {isCurrentMonth && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  Current Month
                </span>
              )}
              {!isCurrentMonth && (
                <button onClick={goCurrentMonth}
                  className="text-[10px] text-blue-600 hover:underline font-semibold">
                  Jump to Current Month
                </button>
              )}
              <span className="text-xs text-gray-500">
                {monthFilteredFees.length} record{monthFilteredFees.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 mt-2">
              <select className="p-1 border rounded text-xs" value={viewMonth}
                onChange={(e) => setViewMonth(parseInt(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <select className="p-1 border rounded text-xs" value={viewYear}
                onChange={(e) => setViewYear(parseInt(e.target.value))}>
                {[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
          <button onClick={goNextMonth}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 font-medium border">
            Next <i className="fas fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
        <div className="bg-white p-3 sm:p-4 rounded shadow border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">Total Fees</p>
          <p className="text-lg sm:text-2xl font-bold">{CURRENCY}{totalAmount.toFixed(2)}</p>
        </div>
        <div className="bg-green-50 p-3 sm:p-4 rounded shadow border-l-4 border-green-500">
          <p className="text-xs text-green-600">Collected</p>
          <p className="text-lg sm:text-2xl font-bold text-green-700">{CURRENCY}{totalPaid.toFixed(2)}</p>
        </div>
        <div className="bg-yellow-50 p-3 sm:p-4 rounded shadow border-l-4 border-yellow-500">
          <p className="text-xs text-yellow-600">Pending</p>
          <p className="text-lg sm:text-2xl font-bold text-yellow-700">{CURRENCY}{totalPending.toFixed(2)}</p>
        </div>
        <div className="bg-red-50 p-3 sm:p-4 rounded shadow border-l-4 border-red-500">
          <p className="text-xs text-red-600">Overdue Count</p>
          <p className="text-lg sm:text-2xl font-bold text-red-700">{overdueCount}</p>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded mb-4 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-blue-800">
            <i className="fas fa-check-square mr-1"></i>
            <strong>{selectedCount}</strong> fee(s) selected · Current balance:{' '}
            <strong>{CURRENCY}{selectedBalance.toFixed(2)}</strong>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1 text-sm bg-white text-gray-700 rounded border border-gray-300 hover:bg-gray-50">
              Clear
            </button>
            <button onClick={handleOpenBulkChallans}
              className="px-4 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
              <i className="fas fa-file-invoice-dollar mr-1"></i> Download Challans
            </button>
            <button onClick={handleBulkMarkPaid}
              className="px-4 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700">
              <i className="fas fa-check-double mr-1"></i> Mark All Paid
            </button>
          </div>
        </div>
      )}

      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        <select className="p-2 border rounded text-sm" value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Overdue">Overdue</option>
          <option value="Paid">Paid</option>
        </select>
        <select className="p-2 border rounded text-sm" value={filterType}
          onChange={(e) => setFilterType(e.target.value)}>
          <option value="all">All Fee Types</option>
          {FEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="p-2 border rounded text-sm" value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(c => (
            <option key={c.id} value={c.id}>{c.name}{c.section ? ` · ${c.section}` : ''}</option>
          ))}
        </select>
        <input type="text"
          placeholder="Search by name, registration #, roll # or fee type…"
          className="flex-1 min-w-[220px] p-2 border rounded text-sm"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <button onClick={fetchData}
          className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
          <i className="fas fa-sync-alt mr-1"></i> Refresh
        </button>
        <span className="ml-auto text-sm text-gray-500">
          Showing {filtered.length} of {monthFilteredFees.length}
        </span>
      </div>

      <div className="bg-white p-4 rounded shadow">
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-inbox text-4xl mb-3"></i>
            <p>No fee records for <strong>{MONTHS[viewMonth - 1]} {viewYear}</strong>.</p>
            <button onClick={openGenerateModal}
              className="mt-3 text-blue-600 hover:underline text-sm">
              Generate fees for {MONTHS[viewMonth - 1]} {viewYear} →
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(col => (
                    <th key={col} className="p-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                      {col === '' ? (
                        <input type="checkbox" className="w-4 h-4 cursor-pointer"
                          checked={allSelected} onChange={handleToggleAll}
                          title="Select all pending" />
                      ) : col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}
                    className={`border-t ${selectedIds.has(row._feeId) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                    {columns.map(col => (
                      <td key={col} className="p-3 text-gray-800 whitespace-nowrap">
                        {row[col] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* GENERATE FEES MODAL */}
      <Modal
        isOpen={genModal}
        onClose={() => setGenModal(false)}
        title="⚡ Generate Fees"
        onSave={handleGenerateBatch}
        saveLabel={genLoading ? 'Generating…' : 'Generate'}
      >
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 p-3 rounded text-sm text-purple-800">
            <i className="fas fa-info-circle mr-1"></i>
            Generates a <strong>single combined fee row</strong> for every active student in the chosen class
            (or all classes). Select multiple fee types below to combine them into one challan.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Fee Types * (Select one or more)</label>
              <div className="grid grid-cols-2 gap-2 border p-3 rounded bg-gray-50 max-h-40 overflow-y-auto">
                {FEE_TYPES.map(t => (
                  <label key={t} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 p-1 rounded">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={genForm.fee_types.includes(t)}
                      onChange={(e) => {
                        const newTypes = e.target.checked
                          ? [...genForm.fee_types, t]
                          : genForm.fee_types.filter(ft => ft !== t);
                        setGenForm({ ...genForm, fee_types: newTypes });
                      }}
                    />
                    {t}
                  </label>
                ))}
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Class</label>
              <select className="w-full p-2 border rounded" value={genForm.class_id}
                onChange={e => setGenForm({ ...genForm, class_id: e.target.value })}>
                <option value="">All Classes</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.section ? ` · ${c.section}` : ''}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Month *</label>
              <select className="w-full p-2 border rounded" value={genForm.month}
                onChange={e => {
                  const m = parseInt(e.target.value);
                  const y = genForm.year;
                  const day = genForm.due_date ? genForm.due_date.split('-')[2] : '05';
                  setGenForm({ ...genForm, month: m, due_date: `${y}-${String(m).padStart(2, '0')}-${day}` });
                }}>
                {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Year *</label>
              <input type="number" className="w-full p-2 border rounded" value={genForm.year}
                onChange={e => {
                  const y = parseInt(e.target.value) || now.getFullYear();
                  const m = String(genForm.month).padStart(2, '0');
                  const day = genForm.due_date ? genForm.due_date.split('-')[2] : '05';
                  setGenForm({ ...genForm, year: y, due_date: `${y}-${m}-${day}` });
                }} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Due Date *</label>
              <input type="date" className="w-full p-2 border rounded"
                value={genForm.due_date}
                onChange={e => setGenForm({ ...genForm, due_date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Amount</label>
            <div className="flex flex-wrap gap-2 mb-2">
              <button type="button"
                onClick={() => setGenForm({ ...genForm, amount_mode: 'structure' })}
                className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                  genForm.amount_mode === 'structure'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>
                <i className="fas fa-layer-group mr-1"></i> Use Class Fee Structure
              </button>
              <button type="button"
                onClick={() => setGenForm({ ...genForm, amount_mode: 'custom' })}
                className={`px-3 py-1.5 rounded text-xs font-semibold border ${
                  genForm.amount_mode === 'custom'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>
                <i className="fas fa-keyboard mr-1"></i> Custom Amount
              </button>
            </div>
            {genForm.amount_mode === 'custom' ? (
              <input type="number" min="0" step="0.01"
                placeholder={`Total amount per student (${CURRENCY.trim()})`}
                className="w-full p-2 border rounded" value={genForm.amount}
                onChange={e => setGenForm({ ...genForm, amount: e.target.value })} />
            ) : (
              <p className="text-xs text-gray-500 italic">
                Amount is picked from the fee structure of the selected fee types and class.
              </p>
            )}
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={genForm.apply_discounts}
              onChange={e => setGenForm({ ...genForm, apply_discounts: e.target.checked })}
              className="w-4 h-4" />
            Apply student-level discounts (if any)
          </label>
          <div>
            <label className="block text-sm font-medium mb-1">Notes (optional)</label>
            <input className="w-full p-2 border rounded"
              placeholder="e.g., Transport fee for October"
              value={genForm.notes}
              onChange={e => setGenForm({ ...genForm, notes: e.target.value })} />
          </div>
        </div>
      </Modal>

      {/* ADD FEE MODAL */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add Fee Record"
        onSave={handleCreateFee}
        saveLabel="Create"
      >
        <form onSubmit={handleCreateFee} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Student</label>
            <select className="w-full p-2 border rounded" value={form.student_id}
              onChange={(e) => setForm({ ...form, student_id: e.target.value })} required>
              <option value="">— Select a student —</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>
                  {s.student_name} ({s.class_name}) · {s.registration_number || 'no reg#'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fee Type</label>
            <select className="w-full p-2 border rounded" value={form.fee_type}
              onChange={(e) => setForm({ ...form, fee_type: e.target.value })}>
              {FEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Amount ({CURRENCY.trim()})</label>
              <input className="w-full p-2 border rounded" type="number" min="0" step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <input className="w-full p-2 border rounded" type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <input className="w-full p-2 border rounded" placeholder="Optional"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </form>
      </Modal>

      {/* EDIT FEE MODAL */}
      <Modal
        isOpen={!!editFeeModal}
        onClose={() => setEditFeeModal(null)}
        title={`Edit Fee — ${students.find(s => s.id === editFeeModal?.student_id)?.student_name || ''}`}
        onSave={handleSaveEditFee}
        saveLabel="Save Changes"
      >
        <div className="space-y-3">
          <div className="bg-gray-50 p-3 rounded text-sm">
            <p><strong>Fee Type:</strong> {editFeeModal?.fee_type}</p>
            <p><strong>Status:</strong> {editFeeModal ? deriveStatus(editFeeModal) : ''}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Base Amount ({CURRENCY.trim()})</label>
            <input type="number" className="w-full p-2 border rounded"
              value={editFeeForm.base_amount}
              onChange={e => setEditFeeForm({ ...editFeeForm, base_amount: e.target.value })}
              min="0" step="0.01" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Discount (%)</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {[0, 25, 50, 75, 100].map(p => (
                <button key={p} type="button"
                  onClick={() => setEditFeeForm({ ...editFeeForm, discount_percentage: p })}
                  className={`px-3 py-1 text-xs rounded border font-semibold ${
                    editFeeForm.discount_percentage == p
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>
                  {p}%
                </button>
              ))}
            </div>
            <input type="number" className="w-full p-2 border rounded"
              value={editFeeForm.discount_percentage}
              onChange={e => setEditFeeForm({ ...editFeeForm, discount_percentage: e.target.value })}
              min="0" max="100" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Discount Reason</label>
            <input className="w-full p-2 border rounded"
              placeholder="e.g., Staff child, Scholarship"
              value={editFeeForm.discount_reason}
              onChange={e => setEditFeeForm({ ...editFeeForm, discount_reason: e.target.value })} />
          </div>
          <div className="bg-blue-50 border border-blue-200 p-3 rounded">
            <p className="text-sm text-blue-800">
              <strong>Final amount:</strong>{' '}
              <span className="text-lg font-bold">
                {CURRENCY}{(
                  (parseFloat(editFeeForm.base_amount) || 0) *
                  (1 - (parseFloat(editFeeForm.discount_percentage) || 0) / 100)
                ).toFixed(2)}
              </span>
            </p>
          </div>
        </div>
      </Modal>

      {/* FULL PAYMENT MODAL */}
      <Modal
        isOpen={!!payModal}
        onClose={() => setPayModal(null)}
        title="Record Full Payment"
        onSave={handleRecordFullPayment}
        saveLabel="Confirm Payment"
      >
        {payModal && payStatement && (
          <div className="space-y-3">
            <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
              <p><strong>Student:</strong>{' '}
                {students.find(s => s.id === payModal.student_id)?.student_name || '—'}
              </p>
              <p><strong>Class:</strong>{' '}
                {students.find(s => s.id === payModal.student_id)?.class_name || '—'}
              </p>
            </div>

            <div className="border rounded max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <tbody>
                  {payStatement.months
                    .filter(m => m.items.length > 0)
                    .map(m => (
                      <React.Fragment key={m.monthKey}>
                        <tr className="bg-gray-100">
                          <td colSpan={2} className="px-2 py-1 font-semibold text-gray-700">
                            {m.monthLabel}
                          </td>
                        </tr>
                        {m.items.map((it, i) => (
                          <tr key={i} className={it.kind === 'late' ? 'bg-amber-50' : ''}>
                            <td className={`px-2 py-1 ${it.kind === 'late' ? 'text-amber-800' : ''}`}>
                              {it.label}
                            </td>
                            <td className={`px-2 py-1 text-right ${it.kind === 'late' ? 'text-amber-800 font-semibold' : ''}`}>
                              {CURRENCY}{it.balance.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-2 border-red-600 bg-red-50 px-3 py-3 rounded">
              <span className="text-sm font-semibold text-red-700 uppercase tracking-wide">
                Total to Pay
              </span>
              <span className="text-2xl font-bold text-red-800">
                {CURRENCY}{payStatement.grandTotal.toFixed(2)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Date</label>
                <input className="w-full p-2 border rounded" type="date"
                  value={payment.paid_date}
                  onChange={(e) => setPayment({ ...payment, paid_date: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Method</label>
                <select className="w-full p-2 border rounded" value={payment.payment_method}
                  onChange={(e) => setPayment({ ...payment, payment_method: e.target.value })}>
                  <option>Cash</option>
                  <option>Bank Transfer</option>
                  <option>Cheque</option>
                  <option>Online</option>
                  <option>Card</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-3 rounded text-xs text-blue-800">
              <i className="fas fa-info-circle mr-1"></i>
              This clears <strong>{payStatement.itemsToMarkPaid.length}</strong> outstanding
              fee record(s){payStatement.lateFeesToCreate.length > 0 && (
                <> plus <strong>{payStatement.lateFeesToCreate.length}</strong> computed late fee(s)</>
              )}. Partial payments are not accepted.
            </div>
          </div>
        )}
      </Modal>

      {/* SINGLE FEE CHALLAN MODAL */}
      {challanFee && challanStudent && (
        <FeeChallan
          fee={challanFee}
          child={challanStudent}
          student={students.find(s => s.id === challanFee.student_id)}
          allFees={fees}
          structures={structures}
          onClose={() => setChallanFee(null)}
        />
      )}

      {/* BULK FEE CHALLAN MODAL */}
      {bulkChallans && (
        <BulkFeeChallan items={bulkChallans} onClose={() => setBulkChallans(null)} />
      )}
    </div>
  );
}